import { Component, ViewContainerRef } from "@angular/core";
import { UIService } from "./core/ui/ui.service";
import { DependenciesService } from "./core/dependencies/dependencies.service";
import { SessionService } from "./core/state/session.service";
import { ConfigService } from "./core/state/config.service";
import { Event, Router, RoutesRecognized, RouterOutlet } from "@angular/router";
import { AnalyticsService } from "./core/analytics/analytics.service";
import { isString, isEmpty } from "lodash-es";
import { filter as rxFilter } from "rxjs/operators";
import { GlobalLoadingState } from "./core/types";
import { checkCookie, deleteCookie, getCookie, setCookie } from "./core/utils/cookies";
import { createStyle, measureScrollbar, rootCssClass } from "./core/utils/dom";
import { isTouchDevice, requestIdleCallback } from "./core/utils/navigator";
import { ElementsSharedHeaderComponent } from "./elements/shared/header/elements-shared-header.component";
import { ElementsSharedToastsComponent } from "./elements/shared/toasts/elements-shared-toasts.component";
import { ElementsSharedSwipeTriggerDirective } from "./elements/shared/swipe-trigger/elements-shared-swipe-trigger.directive";
import { ElementsSharedProgressBarComponent } from "./elements/shared/progress-bar/elements-shared-progress-bar.component";

const TRACKING_COOKIE_NAME = "_bgs_track";

@Component({
    selector: "bs-viewer",
    templateUrl: "app.component.html",
    styleUrls: ["app.component.scss"],
    imports: [
        ElementsSharedHeaderComponent,
        RouterOutlet,
        ElementsSharedToastsComponent,
        ElementsSharedSwipeTriggerDirective,
        ElementsSharedProgressBarComponent,
    ],
})
export class AppComponent {
    public isWriteImpersonation = false;
    public globalLoadingState: GlobalLoadingState = {
        active: false,
    };

    constructor(
        private ui: UIService,
        private session: SessionService,
        private router: Router,
        private analytics: AnalyticsService,
        private config: ConfigService,
        compService: DependenciesService,
        vcRef: ViewContainerRef,
    ) {
        ui.setVCR(vcRef);
        rootCssClass("touch-device", isTouchDevice(), true);

        // Set client type cookie (used when header is not sent)
        setCookie("bgs_client_type", "web", 360, "/", this.config.getValue("cookie.main_domain"));

        session.pipe(rxFilter((e) => e.event === "user.changed")).subscribe((e) => {
            if (e.data && e.data.superuser) {
                this.isWriteImpersonation = !e.data.demo && this.session.isImpersonated();
            } else if (!e.data && this.isWriteImpersonation) {
                this.isWriteImpersonation = false;
            }
        });

        session
            .ready()
            .then(
                () => this.router.initialNavigation(),
                () => this.router.initialNavigation(),
            )
            .then(
                () => rootCssClass("application-ready", true, true),
                () => rootCssClass("application-ready", true, true),
            )
            .then(() => compService.loadImports())
            .then(() =>
                requestIdleCallback(
                    () => {
                        this.handleRemoteTracking();
                        compService.updateComponents();
                    },
                    { timeout: 100 },
                ),
            );

        router.events.subscribe((e: Event) => {
            if (e instanceof RoutesRecognized) {
                this.handleImpersonationInRoute(e);
            }
        });

        const sbWidth = measureScrollbar();

        createStyle(":root", "--scrollbar-width:" + sbWidth + "px;");
    }

    private handleImpersonationInRoute(recognizedRoute: RoutesRecognized): void {
        const params = recognizedRoute.state && recognizedRoute.state.root.queryParams;

        if (!isEmpty(params) && params.impersonation) {
            const user = this.session.getUser();

            if (user && user.superuser) {
                try {
                    this.session.setImpersonation(
                        JSON.parse(atob(decodeURIComponent(params.impersonation))),
                        true,
                    );
                } catch (e) {}
            }
        }
    }

    private handleRemoteTracking(): void {
        if (!checkCookie(TRACKING_COOKIE_NAME)) {
            return;
        }

        const trackCookie = getCookie(TRACKING_COOKIE_NAME);

        if (isString(trackCookie) && trackCookie) {
            // This is a part of startup sequence, and we want to be
            // sure it will not fail in any case. Wrapping in with
            // try { ... } catch (e) { ... }
            try {
                if (trackCookie === "signup") {
                    this.analytics.handle("signup", this.session.getUser());
                }
            } catch (e) {}
        }

        deleteCookie(TRACKING_COOKIE_NAME, "/");
    }

    public toggleDrawer(): void {
        this.ui.toggleDrawer();
    }
}
